/**
 * Maps internal tier enum values to user-facing display names.
 * Internal enum: "basic" | "solopreneur" | "reseller"
 * Display:       "Free Plan" | "Starter" | "Solopreneur"
 */

export type SubscriptionTier = "basic" | "solopreneur" | "reseller";

export const TIER_DISPLAY_NAMES: Record<SubscriptionTier, string> = {
  basic: "Free Plan",
  solopreneur: "Starter",
  reseller: "Solopreneur",
};

export const TIER_PRICES: Record<SubscriptionTier, string> = {
  basic: "$0/mo",
  solopreneur: "$27/mo",
  reseller: "$147/mo",
};

export const TIER_BRAND_LIMITS: Record<SubscriptionTier, number | null> = {
  basic: 1,
  solopreneur: 3,
  reseller: null,
};

export function normalizeTier(tier: string | null | undefined): SubscriptionTier {
  if (tier === "solopreneur" || tier === "reseller") {
    return tier;
  }

  return "basic";
}

export function getTierDisplayName(tier: string): string {
  const normalizedTier = normalizeTier(tier);
  return TIER_DISPLAY_NAMES[normalizedTier] || tier.charAt(0).toUpperCase() + tier.slice(1);
}

export function getTierPrice(tier: string): string {
  const normalizedTier = normalizeTier(tier);
  return TIER_PRICES[normalizedTier] || "";
}

export function getTierBrandLimit(tier: string | null | undefined): number | null {
  return TIER_BRAND_LIMITS[normalizeTier(tier)];
}

export function getTierBrandLimitLabel(tier: string | null | undefined): string {
  const limit = getTierBrandLimit(tier);

  if (limit === null) {
    return "Unlimited Brands";
  }

  return `${limit} Brand${limit === 1 ? "" : "s"}`;
}

export function canCreateBrandForTier(tier: string | null | undefined, currentBrandCount: number): boolean {
  const limit = getTierBrandLimit(tier);
  return limit === null || currentBrandCount < limit;
}

export function getBrandLimitReachedMessage(tier: string | null | undefined): string {
  switch (normalizeTier(tier)) {
    case "solopreneur":
      return "Starter plan is limited to 3 brands. Upgrade to Solopreneur to add more brands.";
    case "reseller":
      return "Your current plan supports unlimited brands.";
    case "basic":
    default:
      return "Free plan is limited to 1 brand. Upgrade to Starter to add more brands.";
  }
}
